import { useEffect } from 'react';
import { View, ScrollView, Pressable } from 'react-native';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Check, Flame, Droplet } from 'lucide-react-native';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/drawer';
import { BaseModalProps } from '@/contexts/ModalContext';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import {
  ServingUnit,
  SaveMealDocument,
  GetMealDocument,
  GetMealCategoriesDocument,
  Language,
} from '~/graphql/generated/graphql';
import { FIELD_INPUT_CLASS, MACROS, toNumber } from './macros';
import { MacroDistributionBar } from './MacroDistributionBar';
import { MacroCard } from './MacroCard';
import { ServingUnitsSection } from './ServingUnitsSection';
import type { MealFormData } from './types';

export interface MealFormDialogProps extends BaseModalProps {
  /** ID of meal to edit. If not provided, creates a new meal. */
  mealId?: string;
  /** Called when a meal is successfully saved */
  onMealSaved?: (id: string) => void;
  /** Portal host name for custom dialog placement */
  portalHost?: string;
  /** Barcode to prefill when creating a new meal */
  barcode?: string;
}

const CategoryPill = ({
  category,
  isActive,
  onPress,
}: {
  category: { id: string; title?: Partial<Record<Language, string>> | null };
  isActive: boolean;
  onPress: () => void;
}) => {
  const localizedTitle = useLocalizedValue(category.title);
  return (
    <Pressable
      onPress={onPress}
      className={`rounded-xl px-4 py-2.5 ${
        isActive ? 'bg-accent' : 'bg-surface-elevated'
      }`}
    >
      <Text
        className={`text-sm font-medium ${
          isActive ? 'text-accent-text font-semibold' : 'text-text-secondary'
        }`}
      >
        {localizedTitle}
      </Text>
    </Pressable>
  );
};

export const MealFormDialog = ({
  mealId,
  onClose,
  onMealSaved,
  barcode,
}: MealFormDialogProps) => {
  const { t } = useTranslation('meal');
  const isEditMode = !!mealId;

  // Load existing meal data in edit mode
  const { data: existingMealData } = useQuery(GetMealDocument, {
    variables: { id: mealId as string },
    skip: !mealId,
  });
  const existingMeal = existingMealData?.meal;

  // Query all categories
  const { data: categoriesData } = useQuery(GetMealCategoriesDocument);
  const categories = categoriesData?.mealCategories ?? [];

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    reset,
  } = useForm<MealFormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      brand: '',
      categoryId: '',
      servingUnit: ServingUnit.Grams,
      energyKcal: 0,
      protein: 0,
      carbs: 0,
      fats: 0,
      fibre: 0,
      waterMl: 0,
      barcode: barcode || '',
      servingUnits: [],
    },
  });

  // Load existing meal data when available
  useEffect(() => {
    if (existingMeal && isEditMode) {
      reset({
        name: existingMeal.name || '',
        brand: existingMeal.brand || '',
        categoryId: existingMeal.category?.id || '',
        servingUnit:
          (existingMeal.servingUnit as ServingUnit) || ServingUnit.Grams,
        energyKcal: Number(existingMeal.nutritionalInfo?.energyKcal ?? 0),
        protein: Number(existingMeal.nutritionalInfo?.protein ?? 0),
        carbs: Number(existingMeal.nutritionalInfo?.carbs ?? 0),
        fats: Number(existingMeal.nutritionalInfo?.fats ?? 0),
        fibre: Number(existingMeal.nutritionalInfo?.fibre ?? 0),
        waterMl: Number(existingMeal.nutritionalInfo?.waterMl ?? 0),
        barcode: existingMeal.barcode || '',
        servingUnits: (existingMeal.servingUnits ?? []).map((unit) => ({
          id: unit.id,
          name: unit.name,
          weightGrams: Number(unit.weightGrams),
        })),
      });
    }
  }, [existingMeal, isEditMode, reset]);

  // Save mutation. Evict the cached `meals` (search) field on success so a newly
  // created custom food is never hidden behind a stale — and now disk-persisted —
  // SearchMeals result set; the next search re-fetches and re-persists.
  const [saveMutation, { loading }] = useMutation(SaveMealDocument, {
    update: (cache) => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'meals' });
      cache.gc();
    },
  });

  const onSubmit = async (data: MealFormData) => {
    try {
      const result = await saveMutation({
        variables: {
          id: isEditMode ? mealId : undefined,
          input: {
            name: data.name.trim(),
            brand: data.brand.trim() || null,
            categoryId: data.categoryId || null,
            // The redesigned form expresses nutrition per 100g, so the base
            // serving is fixed to grams with a 1:1 exchange rate.
            servingSize: 100,
            servingUnit: ServingUnit.Grams,
            exchangeRateTo100g: 1,
            isPublic: false, // User-created meals are private by default
            energyKcal: toNumber(data.energyKcal),
            protein: toNumber(data.protein),
            carbs: toNumber(data.carbs),
            fats: toNumber(data.fats),
            fibre: toNumber(data.fibre),
            sugar: null,
            sodium: null,
            // Optional; blank/0 ⇒ NULL ⇒ 0 hydration (unknown water content).
            waterMl: data.waterMl ? toNumber(data.waterMl) : null,
            dataSource: 'manual',
            allergenId: null,
            barcode: data.barcode.trim() || null,
            servingUnits: data.servingUnits
              .filter((u) => u.name.trim() && toNumber(u.weightGrams) > 0)
              .map((u, index) => ({
                id: u.id ?? null,
                name: u.name.trim(),
                weightGrams: toNumber(u.weightGrams),
                sortIndex: index,
              })),
          },
        },
      });

      if (result.data?.saveMeal?.id) {
        onMealSaved?.(result.data.saveMeal.id);
        onClose?.();
      }
    } catch (error) {
      console.error('Failed to save meal:', error);
    }
  };

  const categoryId = useWatch({ control, name: 'categoryId' });
  const energyKcal = toNumber(useWatch({ control, name: 'energyKcal' }));
  const protein = toNumber(useWatch({ control, name: 'protein' }));
  const carbs = toNumber(useWatch({ control, name: 'carbs' }));
  const fats = toNumber(useWatch({ control, name: 'fats' }));

  return (
    <Drawer open={true} onOpenChange={() => onClose()}>
      <DrawerContent>
        <DrawerHeader onClose={onClose}>
          <View className="gap-0.5">
            <Text className="text-lg font-sora-semibold text-text-primary">
              {t('createMeal.title')}
            </Text>
            <Text className="text-sm text-text-secondary">
              {t('createMeal.subtitle')}
            </Text>
          </View>
        </DrawerHeader>

        <DrawerBody>
          <ScrollView
            className="flex-1"
            contentContainerClassName="gap-6 px-5 pb-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Name */}
            <View className="gap-2">
              <Text className="text-sm font-medium text-text-secondary">
                {t('createMeal.fields.name.label')}
              </Text>
              <Controller
                control={control}
                name="name"
                rules={{ required: true, validate: (v) => v.trim().length > 0 }}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState,
                }) => (
                  <Input
                    testID="meal-form-name"
                    placeholder={t('createMeal.fields.name.placeholder')}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    className={`${FIELD_INPUT_CLASS} ${
                      fieldState.error ? 'border border-negative' : 'border-0'
                    }`}
                  />
                )}
              />
            </View>

            {/* Brand */}
            <View className="gap-2">
              <Text className="text-sm font-medium text-text-secondary">
                {t('createMeal.fields.brand.label')}
              </Text>
              <Controller
                control={control}
                name="brand"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    placeholder={t('createMeal.fields.brand.placeholder')}
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    className={`${FIELD_INPUT_CLASS} border-0`}
                  />
                )}
              />
            </View>

            {/* Barcode (read-only, only visible if provided) */}
            {barcode && (
              <View className="gap-2">
                <Text className="text-sm font-medium text-text-secondary">
                  {t('createMeal.fields.barcode.label')}
                </Text>
                <Controller
                  control={control}
                  name="barcode"
                  render={({ field: { value } }) => (
                    <Input
                      value={value}
                      editable={false}
                      className={`${FIELD_INPUT_CLASS} border-0`}
                    />
                  )}
                />
              </View>
            )}

            {/* Category */}
            {categories.length > 0 && (
              <View className="gap-2">
                <Text className="text-sm font-medium text-text-secondary">
                  {t('createMeal.fields.category.label')}
                </Text>
                <View className="flex-row flex-wrap gap-2">
                  {categories.map((category) => (
                    <CategoryPill
                      key={category.id}
                      category={category}
                      isActive={categoryId === category.id}
                      onPress={() =>
                        setValue(
                          'categoryId',
                          categoryId === category.id ? '' : category.id,
                        )
                      }
                    />
                  ))}
                </View>
              </View>
            )}

            {/* Nutrition section */}
            <View className="gap-3">
              <View className="flex-row items-center justify-between">
                <Text className="text-sm font-sora-semibold text-text-primary">
                  {t('createMeal.nutritionTitle')}
                </Text>
                <View className="rounded-full bg-surface-elevated px-3 py-1">
                  <Text className="text-xs text-text-secondary">
                    {t('createMeal.nutritionPer100g')}
                  </Text>
                </View>
              </View>

              {/* Calorie hero */}
              <View className="flex-row items-center justify-between rounded-2xl bg-surface p-4">
                <View className="flex-row items-center gap-3">
                  <View className="h-9 w-9 items-center justify-center rounded-full bg-accent-soft">
                    <Icon as={Flame} size={18} className="text-accent" />
                  </View>
                  <Text className="font-sora-semibold text-text-primary">
                    {t('nutrition.calories.longLabel')}
                  </Text>
                </View>
                <View className="flex-row items-end gap-1">
                  <View className="w-24">
                    <Controller
                      control={control}
                      name="energyKcal"
                      rules={{ required: true, min: 0 }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                          keyboardType="numeric"
                          value={String(value)}
                          onChangeText={(text) => onChange(toNumber(text))}
                          onBlur={onBlur}
                          className="border-0 bg-transparent px-0 text-right text-2xl font-sora-bold text-text-primary"
                          placeholder="0"
                        />
                      )}
                    />
                  </View>
                  <Text className="pb-2 text-sm text-text-muted">
                    {t('nutrition.kcal.unit')}
                  </Text>
                </View>
              </View>

              {/* Macro distribution */}
              <MacroDistributionBar
                protein={protein}
                carbs={carbs}
                fats={fats}
              />

              {/* Macro grid (2x2) */}
              <View className="gap-3">
                {[MACROS.slice(0, 2), MACROS.slice(2, 4)].map(
                  (row, rowIndex) => (
                    <View key={rowIndex} className="flex-row gap-3">
                      {row.map((macro) => (
                        <Controller
                          key={macro.key}
                          control={control}
                          name={macro.key}
                          rules={{ required: true, min: 0 }}
                          render={({
                            field: { onChange, onBlur, value },
                            fieldState,
                          }) => (
                            <MacroCard
                              label={t(macro.labelKey)}
                              dotClass={macro.bgClass}
                              value={String(value)}
                              onChangeText={(text) => onChange(toNumber(text))}
                              onBlur={onBlur}
                              hasError={!!fieldState.error}
                            />
                          )}
                        />
                      ))}
                    </View>
                  ),
                )}
              </View>

              {/* Water content (optional, per 100g, 0–100 ml) */}
              <View className="flex-row items-center justify-between rounded-xl bg-surface-elevated px-4 py-3.5">
                <View className="flex-row items-center gap-2.5">
                  <View className="h-8 w-8 items-center justify-center rounded-lg bg-macro-water-soft">
                    <Icon as={Droplet} size={16} className="text-macro-water" />
                  </View>
                  <Text className="font-inter-medium text-text-primary">
                    {t('nutrition.water')}
                  </Text>
                </View>
                <View className="flex-row items-end gap-1">
                  <View className="w-16">
                    <Controller
                      control={control}
                      name="waterMl"
                      rules={{ min: 0, max: 100 }}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <Input
                          testID="meal-form-water-input"
                          keyboardType="numeric"
                          value={String(value)}
                          onChangeText={(text) => onChange(toNumber(text))}
                          onBlur={onBlur}
                          className="border-0 bg-transparent px-0 text-right font-sora-semibold text-xl text-text-primary"
                          placeholder="0"
                        />
                      )}
                    />
                  </View>
                  <Text className="pb-1 text-sm text-text-muted">ml</Text>
                </View>
              </View>
            </View>

            {/* Serving units */}
            <ServingUnitsSection
              control={control}
              nutrition={{ energyKcal, protein, carbs, fats }}
            />
          </ScrollView>

          {/* Actions */}
          <View className="flex-row gap-2 border-t border-border px-5 pb-5 pt-4">
            <Button
              variant="secondary"
              className="flex-1"
              onPress={onClose}
              disabled={loading}
            >
              <Text>{t('createMeal.actions.cancel')}</Text>
            </Button>
            <Button
              testID="meal-form-save"
              className="flex-1 flex-row gap-2"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || loading}
              loading={loading}
            >
              <Icon as={Check} size={18} className="text-accent-text" />
              <Text>
                {isEditMode
                  ? t('createMeal.actions.update')
                  : t('createMeal.actions.create')}
              </Text>
            </Button>
          </View>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
